import { useState } from 'react';
import UserAvatar from '../UserAvatar';
import MobileSheet from './MobileSheet';
import ConfirmModal from '../ConfirmModal';
import UserManagementMobile from './UserManagementMobile';
import SprintHistoryMobile from './SprintHistoryMobile';
import UserProfileMobile from './UserProfileMobile';
import BacklogMobile from './BacklogMobile';
import TemplatesMobile from './TemplatesMobile';
import MobileItemDetailSheet from './MobileItemDetailSheet';
import MobileItemDetailView from './MobileItemDetailView';
import { MobileAddFormFields, MobileEditFormFields } from './MobileItemFormFields';
import { LogOutIcon, PlusIcon, CalendarIcon, HistoryIcon, UsersIcon, ArchiveIcon, CopyIcon, ArrowLeftIcon, MoreIcon, InboxIcon, TemplateIcon, ChevronDownIcon } from './Icons';
import { TYPE_CONFIG, STATUS_OPTIONS, KANBAN_STATUS_OPTIONS, PRIORITY_CONFIG, RETRO_COLUMNS } from '../../constants';
import RetroColumnPicker from '../shared/RetroColumnPicker';
import { formatDate, findAssignee, buildUserOptions, getAllDescendants } from '../../utils';
import ItemChildren from '../shared/ItemChildren';
import useSprintBoard from '../../hooks/useSprintBoard';
import useMobileItemForm from '../../hooks/useMobileItemForm';
import './mobile.css';

export default function SprintBoardMobile({ user, userProfile, users, onLogout, showToast, refreshUsers, refreshProfile, project, onBackToProjects, onProjectUpdated }) {
  const {
    sprint, items, boardItems, loading, isKanban, isRetro, projectUsers,
    handleStatusChange, handleAssigneeChange, handleChildStatusToggle, handleAddItem, handleUpdateItem, handleDeleteItem, handleEndSprint, reloadItems,
  } = useSprintBoard({ showToast, project, users });

  const [retroColumn, setRetroColumn] = useState(RETRO_COLUMNS[0].status);
  const [retroTitle, setRetroTitle] = useState('');
  const [retroDesc, setRetroDesc] = useState('');

  const {
    selectedItem, setSelectedItem,
    showDetail, editing, showAdd, setShowAdd, showConfirm, setShowConfirm,
    addForm, editForm,
    openDetail, openDirectEdit, startEditing, resetAddForm, closeDetail, buildAddData, buildEditData,
  } = useMobileItemForm({ isKanban });

  const [showUsers, setShowUsers] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showEndConfirm, setShowEndConfirm] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [showBacklog, setShowBacklog] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [expandedFeatures, setExpandedFeatures] = useState({});

  const userOptions = buildUserOptions(projectUsers);

  const handleAdd = async () => {
    if (!addForm.title.trim()) return;
    try {
      await handleAddItem(buildAddData('board'));
      resetAddForm();
      showToast('Item added', 'success');
    } catch (err) {
      showToast(err.message || 'Failed to add item', 'error');
    }
  };

  const handleAddRetroNote = async () => {
    if (!retroTitle.trim()) return;
    try {
      await handleAddItem({ type: 'task', title: retroTitle.trim(), description: retroDesc.trim(), status: retroColumn, priority: null, assigneeId: null, storyPoints: null, parentId: null });
      setRetroTitle('');
      setRetroDesc('');
      setRetroColumn(RETRO_COLUMNS[0].status);
      setShowAdd(false);
      showToast('Note added', 'success');
    } catch (err) {
      showToast(err.message || 'Failed to add note', 'error');
    }
  };

  const handleSaveEdit = async () => {
    if (!editForm.title.trim()) return;
    try {
      await handleUpdateItem(selectedItem.id, buildEditData());
      closeDetail();
      showToast('Item updated', 'success');
    } catch (err) {
      showToast(err.message || 'Failed to update item', 'error');
    }
  };

  const handleDelete = async () => {
    try {
      await handleDeleteItem(selectedItem.id);
      setShowConfirm(false);
      closeDetail();
      showToast('Item deleted', 'success');
    } catch (err) {
      showToast(err.message || 'Failed to delete item', 'error');
    }
  };

  const onEndSprint = async () => {
    try {
      await handleEndSprint();
      setShowEndConfirm(false);
      showToast(isRetro ? 'Retro ended. New retro started.' : 'Sprint ended. New sprint created.', 'success');
    } catch (err) {
      showToast(err.message || 'Failed to end sprint', 'error');
    }
  };

  if (loading) {
    return (
      <div className="mobile-page" style={{ justifyContent: 'center', alignItems: 'center' }}>
        <div className="mobile-loading-spinner" />
        <p>Loading sprint...</p>
      </div>
    );
  }

  if (showHistory) {
    return (
      <SprintHistoryMobile
        onBack={() => setShowHistory(false)}
        showToast={showToast}
        users={projectUsers}
        userProfile={userProfile}
        projectId={project.id}
        projectType={project?.type}
      />
    );
  }

  if (showTemplates) {
    return (
      <TemplatesMobile
        onBack={() => setShowTemplates(false)}
        showToast={showToast}
        userProfile={userProfile}
        sprintId={sprint?.id}
        projectType={project?.type || 'scrum'}
        onItemsImported={() => {
          reloadItems();
          setShowTemplates(false);
        }}
      />
    );
  }

  if (showBacklog) {
    return (
      <BacklogMobile
        items={items}
        onBack={() => setShowBacklog(false)}
        isKanban={isKanban}
        onUpdateItem={async (id, data) => {
          const updated = await handleUpdateItem(id, data);
          showToast('Item updated', 'success');
          return updated;
        }}
        onDeleteItem={async (id) => {
          await handleDeleteItem(id);
          showToast('Item deleted', 'success');
        }}
        onAddItem={async (itemData) => {
          await handleAddItem(itemData);
          showToast('Item added', 'success');
        }}
        showToast={showToast}
        users={projectUsers}
        userProfile={userProfile}
        projectId={project.id}
      />
    );
  }

  if (!sprint) return null;

  const renderStatusGroup = (status, label) => {
    const filtered = boardItems.filter((i) => i.status === status);
    const groupItems = isKanban ? [...filtered].sort((a, b) => (PRIORITY_CONFIG[a.priority]?.order ?? 1) - (PRIORITY_CONFIG[b.priority]?.order ?? 1)) : filtered;
    return (
      <div className="mobile-status-group" data-status={status} key={status}>
        <div className="mobile-status-header">
          <h3>{label}</h3>
          <span className="mobile-status-count">{groupItems.length}</span>
        </div>
        {groupItems.map((item) => {
          if (isRetro) {
            return (
              <div key={item.id} className="mobile-sprint-card" onClick={() => openDetail(item)}>
                <div className="mobile-sprint-card-left">
                  <div>
                    <div className="mobile-sprint-card-title">{item.title}</div>
                    {item.description && (
                      <div className="mobile-sprint-card-desc">{item.description}</div>
                    )}
                  </div>
                </div>
              </div>
            );
          }

          const config = TYPE_CONFIG[item.type] || TYPE_CONFIG.task;
          const Icon = config.icon;
          const assignee = findAssignee(projectUsers, item.assigneeId);
          const isExpandable = item.type === 'feature' || item.type === 'epic';
          const featureExpanded = isExpandable && expandedFeatures[item.id];
          const descendants = isExpandable ? getAllDescendants(item.id, items) : [];
          const doneCount = descendants.filter((d) => d.status === 'done').length;

          return (
            <div key={item.id}>
              <div className={`mobile-sprint-card${isExpandable ? ' mobile-sprint-card--feature' : ''}`} data-type={item.type} onClick={() => openDirectEdit(item)}>
                <div className="mobile-sprint-card-left">
                  <span style={{ color: config.color }}><Icon size={18} /></span>
                  <div>
                    {item.readableId && (
                      <span
                        className="sprint-item-rid"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigator.clipboard.writeText(`${item.readableId}-${item.title}`);
                        }}
                      >
                        {item.readableId} <CopyIcon size={10} />
                      </span>
                    )}
                    <div className="mobile-sprint-card-title">{item.title}</div>
                    {item.description && (
                      <div className="mobile-sprint-card-desc">{item.description}</div>
                    )}
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {isExpandable && descendants.length > 0 && (
                    <span className="feature-progress-badge">{doneCount}/{descendants.length}</span>
                  )}
                  {isKanban && item.priority && (
                    <span className="priority-badge" style={{ background: PRIORITY_CONFIG[item.priority]?.color }}>
                      {PRIORITY_CONFIG[item.priority]?.label}
                    </span>
                  )}
                  {assignee && (
                    <div className="mobile-sprint-card-assignee">
                      <UserAvatar user={assignee} size={22} showStatus />
                    </div>
                  )}
                  {item.storyPoints != null && (
                    <span className="sprint-item-points">{item.storyPoints}</span>
                  )}
                  {isExpandable && descendants.length > 0 && (
                    <button
                      className={`feature-expand-btn${featureExpanded ? ' feature-expand-btn--open' : ''}`}
                      onClick={(e) => { e.stopPropagation(); setExpandedFeatures((prev) => ({ ...prev, [item.id]: !prev[item.id] })); }}
                    >
                      <ChevronDownIcon size={16} />
                    </button>
                  )}
                </div>
              </div>
              {featureExpanded && (
                <ItemChildren item={item} allItems={items} onChildStatusToggle={handleChildStatusToggle} onEditItem={openDirectEdit} />
              )}
            </div>
          );
        })}
        {groupItems.length === 0 && (
          <div className="mobile-sprint-empty">No items</div>
        )}
      </div>
    );
  };

  const activeStatusGroups = isRetro
    ? RETRO_COLUMNS.map((c) => ({ value: c.status, label: c.title }))
    : isKanban ? KANBAN_STATUS_OPTIONS : STATUS_OPTIONS;

  return (
    <div className="mobile-page mobile-sprint-page">
      <div className="mobile-sprint-header">
        {onBackToProjects && (
          <button className="mobile-btn-icon" onClick={onBackToProjects} title="Back to Projects">
            <ArrowLeftIcon size={20} />
          </button>
        )}
        <div className="mobile-sprint-info">
          <strong>{project?.name ? `${project.name}` : ''}</strong>
          <span className="mobile-sprint-dates">
            <CalendarIcon size={12} /> {sprint.name} &middot; {formatDate(sprint.startDate, { includeYear: false })} - {formatDate(sprint.endDate, { includeYear: false })}
          </span>
        </div>
        <div style={{ display: 'flex', gap: '6px', position: 'relative' }}>
          <button className="mobile-btn-icon" onClick={() => setShowProfile(true)} title="My Profile">
            <UserAvatar user={userProfile} size={28} showStatus={false} />
          </button>
          <button className="mobile-btn-icon" onClick={() => setShowMenu((v) => !v)} title="Menu">
            <MoreIcon size={20} />
          </button>
          {showMenu && (
            <>
              <div className="mobile-dropdown-backdrop" onClick={() => setShowMenu(false)} />
              <div className="mobile-dropdown-menu">
                <button className="mobile-dropdown-item" onClick={() => { setShowMenu(false); setShowHistory(true); }}>
                  <HistoryIcon size={18} /> {isRetro ? 'Retro History' : 'Sprint History'}
                </button>
                {!isRetro && (
                  <button className="mobile-dropdown-item" onClick={() => { setShowMenu(false); setShowTemplates(true); }}>
                    <TemplateIcon size={18} /> Templates
                  </button>
                )}
                {userProfile?.role === 'admin' && (
                  <button className="mobile-dropdown-item" onClick={() => { setShowMenu(false); setShowUsers(true); }}>
                    <UsersIcon size={18} /> User Management
                  </button>
                )}
                <div className="mobile-dropdown-divider" />
                <button className="mobile-dropdown-item danger" onClick={() => { setShowMenu(false); setShowEndConfirm(true); }}>
                  <ArchiveIcon size={18} /> {isRetro ? 'End Retro' : 'End Sprint'}
                </button>
                <button className="mobile-dropdown-item" onClick={() => { setShowMenu(false); onLogout(); }}>
                  <LogOutIcon size={18} /> Logout
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="mobile-sprint-body">
        {activeStatusGroups.map((opt) => renderStatusGroup(opt.value, opt.label))}
      </div>

      <div className="mobile-fab-group">
        {!isRetro && (
          <button className="mobile-fab-backlog" onClick={() => setShowBacklog(true)}>
            <InboxIcon size={18} /> Backlog
          </button>
        )}
        <button className="mobile-fab" onClick={() => setShowAdd(true)}>
          <PlusIcon size={24} />
        </button>
      </div>

      {showAdd && isRetro && (
        <MobileSheet title="Add Retro Note" onClose={() => setShowAdd(false)}>
          <div className="mobile-form">
            <div className="mobile-form-group">
              <label className="mobile-form-label">Column</label>
              <div className="retro-col-picker">
                <RetroColumnPicker
                  value={retroColumn}
                  onChange={setRetroColumn}
                  getButtonClass={(v, active) =>
                    `retro-col-chip retro-col-chip--${v}${active ? ' active' : ''}`
                  }
                />
              </div>
            </div>
            <div className="mobile-form-group">
              <label className="mobile-form-label">Title</label>
              <input
                className="mobile-form-input"
                value={retroTitle}
                onChange={(e) => setRetroTitle(e.target.value)}
                placeholder="Brief summary..."
                autoFocus
              />
            </div>
            <div className="mobile-form-group">
              <label className="mobile-form-label">Note</label>
              <textarea
                className="mobile-form-input retro-note-textarea"
                rows={7}
                value={retroDesc}
                onChange={(e) => setRetroDesc(e.target.value)}
                placeholder="Explain in detail..."
              />
            </div>
            <button className="mobile-btn mobile-btn-primary" onClick={handleAddRetroNote} disabled={!retroTitle.trim()}>
              Add Note
            </button>
          </div>
        </MobileSheet>
      )}

      {showAdd && !isRetro && (
        <MobileSheet title="Add Item" onClose={() => setShowAdd(false)}>
          <div className="mobile-form">
            <MobileAddFormFields form={addForm} userOptions={userOptions} isKanban={isKanban} />
            <button className="mobile-btn mobile-btn-primary" onClick={handleAdd} disabled={!addForm.title.trim()}>
              Add Item
            </button>
          </div>
        </MobileSheet>
      )}

      {showDetail && selectedItem && (
        <MobileItemDetailSheet
          editing={editing}
          onClose={closeDetail}
          onDelete={() => setShowConfirm(true)}
          onSave={handleSaveEdit}
          onEdit={startEditing}
        >
          {editing ? (
            <MobileEditFormFields form={editForm} userOptions={userOptions} isKanban={isKanban} />
          ) : (
            <MobileItemDetailView
              item={selectedItem}
              users={projectUsers}
              isKanban={isKanban}
              onStatusChange={(id, val) => {
                handleStatusChange(id, val);
                setSelectedItem({ ...selectedItem, status: val });
              }}
              onAssigneeChange={(id, val) => {
                handleAssigneeChange(id, val);
                setSelectedItem({ ...selectedItem, assigneeId: val || null });
              }}
            />
          )}
        </MobileItemDetailSheet>
      )}

      {showUsers && (
        <UserManagementMobile
          users={users}
          onClose={() => setShowUsers(false)}
          onUsersChanged={refreshUsers}
          showToast={showToast}
        />
      )}

      {showConfirm && (
        <ConfirmModal
          message="Are you sure you want to delete this item? This action cannot be undone."
          onConfirm={handleDelete}
          onCancel={() => setShowConfirm(false)}
        />
      )}

      {showEndConfirm && (
        <ConfirmModal
          message={isRetro
            ? 'End this retrospective? All notes will be saved to history.'
            : "End this sprint? Completed items will be archived. Uncompleted items will move to the next sprint's backlog."}
          onConfirm={onEndSprint}
          onCancel={() => setShowEndConfirm(false)}
          confirmLabel={isRetro ? 'End Retro' : 'End Sprint'}
        />
      )}

      {showProfile && (
        <UserProfileMobile
          userProfile={userProfile}
          onClose={() => setShowProfile(false)}
          onProfileUpdated={() => { refreshProfile(); refreshUsers(); }}
          showToast={showToast}
        />
      )}
    </div>
  );
}
